import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import FetchStatus from 'common/types/FetchStatus';
import { AppThunk } from 'configureStore';
import { loadProfile } from 'features/profile-settings/ProfileSlice';
import { DateTime } from 'luxon';
import ReactGA from 'react-ga4';

export type Auth = {
  id: number;
  owner: string;
  text: string;
};

type AuthState = {
  access_token: string | null;
  email: string;
  token_type: string;
  expires_in: number;
  scope: string;
  datetime_set: string;
  fetchStatus: FetchStatus;
};

export const initialState: AuthState = {
  access_token: null,
  fetchStatus: FetchStatus.none,
  email: 'placeholder@email.com', // TODO move to profile slice
  token_type: '',
  expires_in: 0,
  scope: '',
  datetime_set: '',
};

export type LogInActionPayload = {
  email: string;
  access_token: string;
  token_type: string;
  expires_in: number;
  scope: string;
};

type ErrorType = {
  message: string;
};

export const authKey = 'chs_external_auth';

const discardCredentials = () => {
  localStorage.removeItem(authKey);
};

const googleLogin = () => {
  ReactGA.event({
    category: 'Authentication',
    action: 'Logged In',
  });
};

const googleLogout = () => {
  ReactGA.event({
    category: 'Authentication',
    action: 'Logged Out',
  });
};

const AuthSlice = createSlice({
  name: 'Auth',
  initialState,
  reducers: {
    loginStart(state) {
      state.fetchStatus = FetchStatus.loading;
      return state;
    },
    loginSuccess(state, action: PayloadAction<LogInActionPayload>) {
      state.fetchStatus = FetchStatus.loaded;
      state.access_token = action.payload.access_token;
      state.token_type = action.payload.token_type;
      state.expires_in = action.payload.expires_in;
      state.scope = action.payload.scope;
      state.email = action.payload.email;
      state.datetime_set = DateTime.local().toISO();
      localStorage.setItem(authKey, JSON.stringify(state));
      return state;
    },
    loginFailure() {
      discardCredentials();
      return { ...initialState, fetchStatus: FetchStatus.loaded };
    },
    logOut() {
      googleLogout();
      return { ...initialState, fetchStatus: FetchStatus.loaded };
    },
    clear() {
      return initialState;
    },
  },
});

async function authenticate(username: string, password: string) {
  const endpoint = `${process.env.REACT_APP_OUATH_BASE}/token`;
  let res, data, error;
  try {
    res = await fetch(endpoint, {
      body: new URLSearchParams({
        username,
        password,
        grant_type: 'password',
        scope: 'write',
        client_id: 'chicagohouseshare',
        client_secret: 'A87yjMwyYBMu5hfVLB',
      }),
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    data = await res.json();
  } catch (err) {
    error = err;
  }

  return { res, data, error };
}

export function attemptLogin(
  email: string,
  password: string
): AppThunk<Promise<boolean | string>> {
  return async function (dispatch) {
    dispatch(AuthSlice.actions.loginStart());
    const { res, data, error } = await authenticate(email, password);
    let message = '';

    if (res?.ok) {
      const { access_token, token_type, expires_in, scope } = data;
      if (access_token && expires_in && scope === 'write') {
        const auth = {
          email,
          access_token,
          token_type,
          expires_in,
          scope,
        };
        // Load profile information before dispatching success
        await dispatch(loadProfile(access_token));
        // Dispatch success and set auth information
        dispatch(AuthSlice.actions.loginSuccess(auth));
        googleLogin();
        return Promise.resolve(true);
      }
      message =
        'Please contact an administrator with the following message: invalid response.';
    }

    if (!message && error) {
      message = (error as ErrorType).message;
    }
    if (!message && data) {
      if (data.error_description) message = data.error_description;
      else if (data.error) message = data.error;
    }
    if (!message) {
      message = 'Please try again later.';
    }
    dispatch(AuthSlice.actions.loginFailure());
    return Promise.reject(message);
  };
}

export function tryAutoLogin(): AppThunk<Promise<boolean>> {
  return async function (dispatch) {
    dispatch(AuthSlice.actions.loginStart());
    const storage = localStorage.getItem(authKey);

    if (!storage) {
      dispatch(AuthSlice.actions.loginFailure());
      return Promise.resolve(false);
    }
    const auth: AuthState = JSON.parse(storage);
    if (!auth.access_token) {
      dispatch(AuthSlice.actions.loginFailure());
      return Promise.resolve(false);
    }
    const time = DateTime.fromISO(auth.datetime_set);
    if (!time.isValid) {
      dispatch(AuthSlice.actions.loginFailure());
      return Promise.resolve(false);
    }
    const { seconds: ellapsed } = DateTime.local()
      .diff(time, 'second')
      .toObject();
    const expires_in = Math.round(auth.expires_in - (ellapsed || 0));
    if (expires_in <= 0) {
      dispatch(AuthSlice.actions.loginFailure());
      return Promise.resolve(false);
    }

    try {
      // Load profile information using previous access token
      await dispatch(loadProfile(auth.access_token));
    } catch (e) {
      return Promise.resolve(false);
    }

    dispatch(
      AuthSlice.actions.loginSuccess({
        email: auth.email,
        access_token: auth.access_token,
        token_type: auth.token_type,
        scope: auth.scope,
        expires_in,
      })
    );
    return Promise.resolve(true);
  };
}

export function logOut(): AppThunk<void> {
  return async function (dispatch) {
    discardCredentials();
    dispatch(AuthSlice.actions.logOut());
  };
}

export default AuthSlice.reducer;
